import React from 'react';
import { mockWorkers } from '../mock';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Star, MapPin, ArrowRight } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';

const WorkerProfiles = () => {
  const navigate = useNavigate();

  return (
    <section className="py-24 bg-[#F0F0F2] dark:bg-neutral-900" data-testid="worker-profiles-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-[#2B4C6F] text-white rounded-full px-4 py-2 mb-4 border-2 border-black shadow-[2px_2px_0px_#000]">
            <span className="text-sm font-bold">Featured Workers</span>
            <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full border border-white/30">Preview</span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-neutral-900 dark:text-white mb-4">
            Find Skilled Construction Workers
          </h2>
          <p className="text-lg text-neutral-600 dark:text-neutral-300 max-w-3xl mx-auto">
            Browse profiles of experienced professionals ready to join your project. Filter by skills, location, and availability.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {mockWorkers.map((worker, index) => (
            <div 
              key={worker._id || worker.id || index} 
              className="bg-white dark:bg-neutral-800 rounded-xl overflow-hidden border-2 border-black dark:border-neutral-700 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F] hover:translate-y-[-4px] hover:shadow-[4px_8px_0px_#000] dark:hover:shadow-[4px_8px_0px_#2B4C6F] transition-all duration-300"
              data-testid={`worker-card-${index}`}
            >
              <div className="p-6">
                <div className="flex items-start space-x-4 mb-4">
                  <div className="w-16 h-16 rounded-xl overflow-hidden border-2 border-black dark:border-neutral-600 shadow-[2px_2px_0px_#000] dark:shadow-[2px_2px_0px_#2B4C6F] flex-shrink-0">
                    <img 
                      src={worker.image} 
                      alt={worker.name} 
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-xl font-bold text-neutral-900 dark:text-white truncate">{worker.name}</h3>
                    <p className="text-neutral-600 dark:text-neutral-400">{worker.title}</p>
                  </div>
                </div>

                <div className="flex items-center text-neutral-600 dark:text-neutral-400 mb-3">
                  <MapPin className="h-4 w-4 mr-1" weight="bold" />
                  <span className="text-sm">{worker.location}</span>
                </div>

                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-2xl font-bold text-[#2B4C6F]">{worker.hourlyRate}</p>
                    <p className="text-sm text-neutral-500 dark:text-neutral-400">/hour</p>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center">
                      <Star className="h-5 w-5 text-amber-500" weight="fill" />
                      <span className="ml-1 font-bold text-neutral-900 dark:text-white">{worker.rating}</span>
                    </div>
                    <p className="text-sm text-neutral-500 dark:text-neutral-400">({worker.reviews} reviews)</p>
                  </div>
                </div>

                <div className="mb-4">
                  <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-2">{worker.experience}</p>
                  <div className="flex flex-wrap gap-2">
                    {worker.skills.map((skill, skillIndex) => (
                      <Badge 
                        key={skillIndex} 
                        variant="secondary" 
                        className="bg-[#2B4C6F]/10 dark:bg-[#2B4C6F]/30 text-[#2B4C6F] dark:text-white border border-[#2B4C6F]/30 font-medium"
                      >
                        {skill}
                      </Badge>
                    ))}
                  </div>
                </div>

                <div className="flex space-x-2">
                  <Button 
                    className="flex-1 bg-[#2B4C6F] hover:bg-[#4A7DA6] text-white font-bold border-2 border-black shadow-[2px_2px_0px_#000] active:translate-y-1 active:shadow-none transition-all h-12"
                    onClick={() => navigate('/find-workers')}
                    data-testid={`contact-worker-${index}`}
                  >
                    Contact
                  </Button>
                  <Button 
                    variant="outline" 
                    className="flex-1 bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white font-bold border-2 border-black dark:border-neutral-600 shadow-[2px_2px_0px_#000] dark:shadow-[2px_2px_0px_#2B4C6F] active:translate-y-1 active:shadow-none transition-all h-12"
                    onClick={() => navigate('/find-workers')}
                    data-testid={`view-profile-${index}`}
                  >
                    View Profile
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center">
          <Button 
            size="lg" 
            className="bg-[#2B4C6F] hover:bg-[#4A7DA6] text-white text-lg px-8 h-16 border-2 border-black shadow-[4px_4px_0px_#000] active:translate-y-1 active:shadow-none transition-all font-bold"
            onClick={() => navigate('/find-workers')}
            data-testid="browse-all-workers-btn"
          >
            Browse All Workers
            <ArrowRight className="ml-2 h-5 w-5" weight="bold" />
          </Button>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-3">View real worker profiles with verified reviews</p>
        </div>
      </div>
    </section>
  );
};

export default WorkerProfiles;
